import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';

/* Each contact item has its own icon animation */
const CONTACT_INFO = [
  {
    icon: MapPin,
    label: 'Location',
    value: 'Bangalore, Karnataka, India',
    href: 'https://maps.app.goo.gl/7eWGcSLPGJAYwnnt8',
    external: true,
    iconAnim: { y: [0, -6, 0, -3, 0], transition: { duration: 0.5 } },
  },
  {
    icon: Phone,
    label: 'Phone',
    value: '+91 8073504269',
    href: 'tel:+918073504269',
    external: false,
    iconAnim: { rotate: [0, -15, 15, -10, 10, 0], transition: { duration: 0.4 } },
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'visyramediaproductions@gmail.com',
    href: 'mailto:visyramediaproductions@gmail.com',
    external: false,
    iconAnim: { scale: [1, 1.3, 1, 1.2, 1], transition: { duration: 0.5 } },
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: '+91 80735 04269',
    href: 'https://wa.me/918073504269',
    external: true,
    iconAnim: { x: [0, -5, 5, -3, 3, 0], transition: { duration: 0.4 } },
  },
];

export default function ContactPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative h-[40vh] sm:h-[50vh] flex items-end overflow-hidden bg-visyra-paper">
        <div className="absolute inset-0 bg-gradient-to-b from-visyra-bg to-visyra-paper" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-3">Reach Out</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="contact-page-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-visyra-text">
            Get in Touch
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="font-body text-base text-visyra-muted mt-3 max-w-xl">
            Whether you're planning a dream wedding, launching a fashion line, or need stunning commercial visuals — we'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-section" className="py-16 sm:py-24 bg-visyra-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info Cards */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <div className="space-y-5">
                {CONTACT_INFO.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <motion.a
                      key={item.label}
                      href={item.href}
                      target={item.external ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      data-testid={`contact-${item.label.toLowerCase()}`}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      className="group flex items-start gap-5 p-5 bg-visyra-card/50 border border-white/5 hover:border-gold/30 transition-all duration-400"
                    >
                      <motion.div
                        whileHover={item.iconAnim}
                        className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-gold-subtle border border-gold/20 group-hover:bg-gold/20 transition-all duration-300"
                      >
                        <Icon size={18} className="text-gold" strokeWidth={1.5} />
                      </motion.div>
                      <div>
                        <p className="font-body text-xs uppercase tracking-widest text-visyra-dim mb-1">{item.label}</p>
                        <p className="font-body text-sm sm:text-base text-visyra-text group-hover:text-gold transition-colors duration-300">{item.value}</p>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>

            {/* Google Maps Embed */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }} className="relative">
              <div className="relative overflow-hidden border border-white/5 h-full min-h-[400px] lg:min-h-[500px]">
                <iframe
                  title="Visyra Location"
                  data-testid="google-maps-embed"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.99907795708!2d77.50058458467011!3d12.84333575512786!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x21c69fdaeae07b07%3A0xa60c0486f74b3b01!2sVISYRA%20MEDIA%20PRODUCTIONS!5e0!3m2!1sen!2sin!4v1771250126205!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="map-grayscale"
                />
                <div className="absolute inset-0 border border-gold/10 pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
